import { UserHosting200RowsItem } from '@newfold/huapi-js/src/index.schemas';

import {
  AI_PRODUCT_AM_REDIRECT_URLS,
  AI_PRODUCT_ROUTE_PREFIXES,
} from './constants';

export const MAX_FAILURE_ATTEMPTS = 20;

export const getRetryDelay = (attemptIndex: number, error: any): number => {
  const status = error?.response?.status;
  if (status === 504) {
    return Math.min(1000 * 2 ** (attemptIndex + 2), 60000);
  }
  return Math.min(1000 * 2 ** attemptIndex, 30000);
};

export const shouldRetry = (
  failureCount: number,
  error: { response?: { status?: number } },
  mfeProvisioning?: boolean,
) => {
  const errorCode = error?.response?.status || 0;
  if (errorCode === 504 || errorCode === 404) {
    return failureCount < MAX_FAILURE_ATTEMPTS;
  }
  if (errorCode === 403 && mfeProvisioning) {
    return failureCount < MAX_FAILURE_ATTEMPTS;
  }
  return false;
};

interface TenantStateParams {
  tenant?: any;
  revampData?: any;
  revampLoading: boolean;
  revampError?: any;
  revampFailureCount: number;
  revampFailureReason?: any;
  revampRefetch?: () => void;
  legacyLoading: boolean;
  legacyPending: boolean;
  legacyError?: any;
  legacyFailureCount: number;
  legacyFailureReason?: any;
  legacyRefetch?: () => void;
  mfeProvisioning?: boolean;
  hostingPackages: UserHosting200RowsItem[];
  isHostingPackagesEmpty: boolean;
}

const matchesAiProductRoutePrefix = (
  pathname: string,
  prefix: (typeof AI_PRODUCT_ROUTE_PREFIXES)[number],
) =>
  pathname === prefix ||
  pathname.startsWith(`${prefix}/`) ||
  pathname.endsWith(prefix) ||
  pathname.includes(`${prefix}/`);

export const isAiProductRoute = (pathname?: string | null) => {
  if (!pathname) return false;

  return AI_PRODUCT_ROUTE_PREFIXES.some((prefix) =>
    matchesAiProductRoutePrefix(pathname, prefix),
  );
};

export const getAiProductRoutePrefix = (
  pathname?: string | null,
): (typeof AI_PRODUCT_ROUTE_PREFIXES)[number] | null => {
  if (!pathname) return null;

  return (
    AI_PRODUCT_ROUTE_PREFIXES.find((prefix) =>
      matchesAiProductRoutePrefix(pathname, prefix),
    ) ?? null
  );
};

export const getAiProductAMRedirectUrl = (pathname?: string | null) => {
  const prefix = getAiProductRoutePrefix(pathname);

  return prefix ? AI_PRODUCT_AM_REDIRECT_URLS[prefix] : null;
};

export const getAiProductRouteTenantState = (refetch?: () => void) => {
  return {
    accountHostingList: [] as any[],
    isPending: false,
    isError: false,
    failureCount: 0,
    failureReason: undefined,
    refetch: refetch ?? (() => {}),
    isValidated: true,
    isEmpty: false,
    isTimeoutError: false,
    isUnexpectedError: false,
    currentProvisioning: false,
    isProvisioningExtended: false,
    showTenantError: false,
  };
};

export function getAMRevampState({
  revampData,
  revampLoading,
  revampError,
  revampFailureCount,
  revampFailureReason,
  revampRefetch,
  hostingPackages,
  isHostingPackagesEmpty,
}: TenantStateParams) {
  const accountHostingList: any[] = [];
  const isPending = revampLoading;
  const isError = revampError;
  const failureCount = revampFailureCount;
  const failureReason = revampFailureReason;
  const refetch = revampRefetch ?? (() => {});

  const isValidated = hostingPackages.length > 0;
  const isEmpty = isHostingPackagesEmpty;
  const isTimeoutError = failureReason?.response?.status === 504;

  const isUnexpectedError =
    !revampLoading &&
    (revampError || !revampData || revampFailureCount > MAX_FAILURE_ATTEMPTS);

  const showTenantError = isUnexpectedError || isTimeoutError;

  return {
    accountHostingList,
    isPending,
    isError,
    failureCount,
    failureReason,
    refetch,
    isValidated,
    isEmpty,
    isTimeoutError,
    isUnexpectedError,
    currentProvisioning: false,
    isProvisioningExtended: false,
    showTenantError,
  };
}

export function getLegacyState({
  tenant,
  legacyLoading,
  legacyPending,
  legacyError,
  legacyFailureCount,
  legacyFailureReason,
  legacyRefetch,
  mfeProvisioning,
}: TenantStateParams) {
  const accountHostingList = tenant?.data?.accounts ?? [];
  const isPending = legacyPending;
  const isError = legacyError;
  const failureCount = legacyFailureCount;
  const failureReason = legacyFailureReason;
  const refetch = legacyRefetch ?? (() => {});

  const isValidated = accountHostingList.length > 0;
  const isEmpty =
    !isPending && tenant?.data?.accounts && accountHostingList.length < 1;
  const isTimeoutError = failureReason?.response?.status === 504;

  const isUnexpectedError =
    !legacyLoading &&
    !legacyPending &&
    (legacyError || !tenant?.data || legacyFailureCount > MAX_FAILURE_ATTEMPTS);

  const currentProvisioning = mfeProvisioning;

  const isProvisioningExtended =
    !!currentProvisioning &&
    [403, 404].includes(failureReason?.response?.status ?? 0) &&
    failureCount <= MAX_FAILURE_ATTEMPTS;

  const showTenantError =
    currentProvisioning || isUnexpectedError || isTimeoutError;

  return {
    accountHostingList,
    isPending,
    isError,
    failureCount,
    failureReason,
    refetch,
    isValidated,
    isEmpty,
    isTimeoutError,
    isUnexpectedError,
    currentProvisioning,
    isProvisioningExtended,
    showTenantError,
  };
}
